import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useOffers } from "@nju/data-access-portal-offers";
import { useOrchestrators } from "@nju/data-access-portal-orchestrator";
import { OnboardingMembersInfo } from "@nju/feature-portal-msisdn";
import { CancelOrderModal } from "@nju/feature-sales-order";
import { Icon } from "@nju/icons";
import { portalRoutes, salesRoutes } from "@nju/routes";
import { IScope } from "@nju/scope";
import {
  A,
  Button,
  ButtonsWrapper,
  Modal,
  Separator,
  Text,
  useOverlay,
} from "@nju/ui";
import { goToNextNumberMachine } from "./goToNextNumber.machine";
import { PortalCopy, SalesCopy } from "./partials";

interface IGoToNextNumberProps {
  variant?: IScope;
}

export function GoToNextNumber({ variant = "portal" }: IGoToNextNumberProps) {
  const { multiOrder } = useOrchestrators();
  const { mobile: mobileOffers } = useOffers();
  const offer = mobileOffers.indefiniteOffers.find((offer) => {
    return offer.display;
  });
  const router = useRouter();
  const cancelOrderModal = useOverlay();
  const { screenVariant: numberVariant } = portalRoutes.idziemyDalej
    .input(router.query)
    .unwrap();

  const redirectUrl =
    variant !== "sales"
      ? portalRoutes.czyZachowacNumer.get({
          mode: "mobile",
          oferta: offer?.offerId,
        })
      : salesRoutes.czyZachowacNumer.get({
          mode: "mobile",
          oferta: offer?.offerId,
        });

  const [_, send] = useMachine(goToNextNumberMachine, {
    actions: {
      redirectToSelectNewOrExistingNumber: () => {
        router.push(redirectUrl);
      },
    },
    context: {
      orchestratorId: multiOrder?.id,
    },
  });

  const handleRetryAction = React.useCallback(() => {
    send({ type: "TRY_AGAIN" });
  }, [send]);

  return (
    <>
      {variant !== "sales" && (
        <OnboardingMembersInfo
          isMulti={false}
          multiOrder={multiOrder}
          screenVariant={numberVariant}
        />
      )}
      <div className="my-8">
        <div className="flex justify-between mt-8">
          <div className="max-w-[710px] w-full">
            <Text
              display="block"
              size={{
                "@onlyMobile": "keyS",
                "@fromTablet": "keyM",
              }}
              weight="bold"
            >
              idziemy dalej{numberVariant === "failed" ? "?" : ""}
            </Text>
            {variant !== "sales" && (
              <PortalCopy numberVariant={numberVariant} />
            )}
            {variant === "sales" && <SalesCopy numberVariant={numberVariant} />}
            <Separator size="xxl" />
            <div
              className="only-mobile:flex only-mobile:flex-col"
              data-cy="next-button-section"
            >
              {(!numberVariant || numberVariant === "success") && (
                <Link href={redirectUrl} passHref={true}>
                  <A
                    display={{
                      "@fromTablet": "inline",
                    }}
                    size={{
                      "@onlyMobile": "s",
                    }}
                    variant="primary"
                  >
                    przejdź dalej
                  </A>
                </Link>
              )}
              {numberVariant === "failed" && (
                <ButtonsWrapper withSeparator={false}>
                  {variant === "sales" && (
                    <Button
                      display={{
                        "@fromTablet": "inline",
                      }}
                      onPress={cancelOrderModal.open}
                      size={{
                        "@onlyMobile": "s",
                      }}
                      variant="secondary"
                    >
                      anuluj zamówienie
                    </Button>
                  )}
                  <Button
                    display={{
                      "@fromTablet": "inline",
                    }}
                    onPress={handleRetryAction}
                    size={{
                      "@onlyMobile": "s",
                    }}
                    variant="primary"
                  >
                    {variant !== "sales" ? "przejdź dalej" : "zmień numer"}
                  </Button>
                </ButtonsWrapper>
              )}
              {multiOrder && cancelOrderModal.isOpened && (
                <Modal
                  onClose={cancelOrderModal.close}
                  title="anulowanie zamówienia"
                >
                  <CancelOrderModal
                    mode="mobile"
                    onClose={cancelOrderModal.close}
                    orchestratorId={multiOrder.id}
                  />
                </Modal>
              )}
            </div>
          </div>
          <div className="hidden from-desktop:block mr-10">
            {numberVariant === "failed" && (
              <Icon
                height={270}
                name="shopping-bag-incircle-fail"
                width={270}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
